import React, { FC, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import Dialog from 'components/Dialog';
import { Button, LoaderSpinner, PercentInput, TextField } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT, TIME_FORMAT } from 'globals/constants';
import { exportAttendanceDetails } from 'services/course-api';

interface ExportAttendanceProps {
  isOpen: boolean;
  classId: string | undefined;
  attendanceDetails: ClassAttendanceDetails | null;
  onClose(hasExported: boolean): void;
}

interface SetPenaltyAction {
  type: 'SET_PENALTY';
  userId: string;
  amount: number | undefined;
}

const individualPenaltyReducer = (state: HashMap<number | undefined>, action: SetPenaltyAction) => {
  switch (action.type) {
    case 'SET_PENALTY':
      return {
        ...state,
        [action.userId]: action.amount,
      };
    default:
      throw new Error('Invalid action');
  }
};

const ExportAttendance: FC<ExportAttendanceProps> = ({ classId, attendanceDetails, isOpen, onClose }) => {
  const [latePenalty, setLatePenalty] = useState<number | undefined>(50);
  const [maxScore, setMaxScore] = useState<number | undefined>(20);
  const [isExporting, setIsExporting] = useState(false);

  const [individualPenaltyMap, setIndividualPenaltyMap] = useReducer(
    individualPenaltyReducer,
    {} as HashMap<number | undefined>
  );

  useEffect(() => {
    attendanceDetails?.studentsData.forEach((s) => {
      setIndividualPenaltyMap({
        type: 'SET_PENALTY',
        userId: s.userId,
        amount: s.status === 'late' ? latePenalty : 0,
      });
    });
  }, [attendanceDetails, latePenalty]);

  const presentStudents = useMemo(
    () =>
      attendanceDetails ? attendanceDetails.studentsData.filter((s) => s.status === 'present').length : 0,
    [attendanceDetails]
  );

  const absentStudents = useMemo(
    () =>
      attendanceDetails
        ? attendanceDetails.studentsData.filter(
            (s) => s.status === 'absent' && attendanceDetails.attendanceRecorded
          ).length
        : 0,
    [attendanceDetails]
  );

  const lateStudents = useMemo(
    () => (attendanceDetails ? attendanceDetails.studentsData.filter((s) => s.status === 'late').length : 0),
    [attendanceDetails]
  );

  const excusedStudents = useMemo(
    () =>
      attendanceDetails ? attendanceDetails.studentsData.filter((s) => s.status === 'excused').length : 0,
    [attendanceDetails]
  );

  const exportAttendanceData = useCallback(async () => {
    if (!classId) return;
    setIsExporting(true);

    const particulars: { userId: string; latePenalty: number }[] = [];

    for (const [userId, amount] of Object.entries(individualPenaltyMap)) {
      particulars.push({
        userId,
        latePenalty: amount || 0,
      });
    }

    try {
      await exportAttendanceDetails({
        activityId: classId,
        activityType: 'attendance',
        latePenalty: latePenalty!,
        maxScore: maxScore!,
        particulars,
      });
      onClose(true);
    } catch (err) {
      console.error(err);
      onClose(false);
    }
  }, [classId, individualPenaltyMap, latePenalty, maxScore, onClose]);

  const getRowData = useCallback(
    (s: StudentAttendance) => {
      switch (s.status) {
        case 'present':
          return {
            style: 'good',
            status: 'Present',
            score: '100%',
            penalty: '0',
            result: '100%',
          };
        case 'excused':
          return {
            style: 'ok',
            status: 'Excused',
            score: '100%',
            penalty: (
              <PercentInput
                wrapperClassName='activity__score-input-wrapper'
                selector='activity__score-input'
                initValue={0}
                onchange={(value) =>
                  setIndividualPenaltyMap({
                    type: 'SET_PENALTY',
                    userId: s.userId,
                    amount: +(value || 0),
                  })
                }
                placeholder='Penalty'
              />
            ),
            result: `${100 - (individualPenaltyMap[s.userId] || 0)}%`,
          };
        case 'late':
          return {
            style: 'bad',
            status: 'Late',
            score: '100%',
            penalty: (
              <PercentInput
                wrapperClassName='activity__score-input-wrapper'
                selector='activity__score-input'
                initValue={latePenalty}
                onchange={(value) =>
                  setIndividualPenaltyMap({
                    type: 'SET_PENALTY',
                    userId: s.userId,
                    amount: +(value || 0),
                  })
                }
                placeholder='Penalty'
              />
            ),
            result: `${100 - (individualPenaltyMap[s.userId] || 0)}%`,
          };
        case 'checkedIn':
          return {
            style: 'dark-grey',
            status: 'Checked In',
            score: '0%',
            penalty: '0',
            result: '0%',
          };
        case 'absent':
          return {
            style: 'light-grey',
            status: 'Absent',
            score: '0%',
            penalty: '0',
            result: '0%',
          };
        default:
          return {
            style: 'light-grey',
            status: '--',
            score: '--',
            penalty: '--',
            result: '--',
          };
      }
    },
    [individualPenaltyMap, latePenalty]
  );

  return (
    <Dialog
      variant='large'
      isOpen={isOpen}
      onClose={() => onClose(false)}
      classes={{ dialog: 'activity', title: 'activity__header' }}
      title={
        <>
          <div className='activity__number'>{`Session ${attendanceDetails?.classNum}`}</div>
          <div className='activity__title'>{attendanceDetails?.classTitle || 'Untitled'}</div>
          <div className='activity__total'>
            {presentStudents} Present, {lateStudents} Late, {excusedStudents} Excused, {absentStudents} Absent
          </div>
        </>
      }
      footer={
        <Button
          variant='outlined'
          color='info'
          size='large'
          disabled={isExporting}
          onClick={exportAttendanceData}
        >
          {isExporting ? 'Exporting...' : 'Export Attendance'}
        </Button>
      }
    >
      {!attendanceDetails ? (
        <div className='loader-wrapper'>
          <LoaderSpinner />
        </div>
      ) : (
        <>
          <div className='activity__details'>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>RECORDED BY</div>
              <div className='activity__detail-value'>{attendanceDetails.recordedBy.name}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>CLASS DATE</div>
              <div className='activity__detail-value'>
                {dt.format(attendanceDetails.startTime, `${DATE_FORMAT}, ${TIME_FORMAT}`)}
              </div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>STUDENTS PRESENT</div>
              <div className='activity__detail-value'>{presentStudents}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>STUDENTS LATE</div>
              <div className='activity__detail-value'>{lateStudents}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>LATE PENALTY</div>
              <div className='activity__detail-value'>
                <PercentInput
                  wrapperClassName='activity__score-input-wrapper'
                  selector='activity__score-input'
                  initValue={latePenalty}
                  onchange={(value) => setLatePenalty(value)}
                  placeholder='Penalty'
                />
              </div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>MAXIMUM SCORE</div>
              <div className='activity__detail-value'>
                <TextField
                  type='number'
                  wrapperClassName='activity__score-input-wrapper'
                  selector='activity__score-input'
                  value={maxScore}
                  onchange={(e) => setMaxScore(+e.target.value)}
                  placeholder='Maximum score'
                />
              </div>
            </div>
          </div>
          <div className='activity__body'>
            <table className='activity__table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Score Attained</th>
                  <th className='activity__penalty-cell'>Late Penalty</th>
                  <th>Final Score</th>
                </tr>
              </thead>
              <tbody>
                {attendanceDetails.studentsData.map((s) => {
                  const data = getRowData(s);
                  return (
                    <tr key={s.userId}>
                      <td>
                        <div className='activity__user'>{s.name}</div>
                      </td>
                      <td className={data.style}>{data.status}</td>
                      <td className={data.style}>{data.score}</td>
                      <td className={data.style}>{data.penalty}</td>
                      <td className={data.style}>{data.result}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default ExportAttendance;
